<template>
  <div class="round-admin">

    <!-- ============ 출발일 목록 ============ -->
    <div v-if="mode === 'list'">
      <div class="round-admin__toolbar">
        <h2 class="round-admin__h2">버스 배차·좌석 관리</h2>
        <div class="round-admin__filter">
          <label>출발일</label>
          <input v-model="dateFrom" type="date" />
          <span>~</span>
          <input v-model="dateTo" type="date" />
          <button type="button" class="btn btn--sm btn--primary" :disabled="loading" @click="loadList">
            {{ loading ? '불러오는 중...' : '조회' }}
          </button>
        </div>
      </div>

      <p class="round-admin__hint round-admin__hint--top">
        예약이 있는 출발일은 자동으로 목록에 표시됩니다. 출발 2일 전까지 <b>투입 차량 수</b>를 정하고 <b>호차별 명단</b>을 등록해 주십시오.
        명단에 예약번호가 있는 예약은 그 호차로, 명단에 없는 예약은 1호차로 배차됩니다.
      </p>
      <p v-if="noLayoutCnt > 0" class="round-admin__warn round-admin__hint--top">
        좌석 레이어가 지정되지 않은 루트의 예약이 있습니다 ({{ noLayoutCnt }}건). 루트 관리에서 레이어를 지정해 주십시오.
      </p>

      <table class="round-admin__table">
        <thead>
          <tr>
            <th>출발일</th>
            <th>루트</th>
            <th>버스</th>
            <th>명단</th>
            <th>탑승 인원 / 정원</th>
            <th>미배차</th>
            <th>좌석 확정</th>
            <th>좌석지정</th>
            <th>마감</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rounds" :key="r.roundId">
            <td>{{ fmtDate(r.roundDate) }}</td>
            <td>{{ r.routeName }}</td>
            <td>{{ r.busTimes || '-' }}</td>
            <td>
              <span :class="Number(r.manifestBusCnt) < Number(r.busCnt) ? 'round-admin__warn' : ''">
                {{ r.manifestBusCnt }} / {{ r.busCnt }}대
              </span>
            </td>
            <td>
              {{ Number(r.manifestBusCnt) > 0 ? r.manifestPax : r.assignedPax }} / {{ r.capCnt }}
              <div class="round-admin__sub">{{ Number(r.manifestBusCnt) > 0 ? '명단 기준' : '홈페이지 예약 기준' }}</div>
            </td>
            <td>
              <span v-if="Number(r.unassignedCnt) > 0" class="round-admin__warn">{{ r.unassignedCnt }}건</span>
              <span v-else class="round-admin__muted">-</span>
            </td>
            <td>{{ r.bookedCnt }}석</td>
            <td><span :class="['badge', openBadge(r.seatOpenFlag).cls]">{{ openBadge(r.seatOpenFlag).label }}</span></td>
            <td>{{ fmtDateTime(r.seatCloseAt) }}</td>
            <td><button type="button" class="btn btn--sm" @click="openRound(r.roundId)">관리</button></td>
          </tr>
          <tr v-if="rounds.length === 0">
            <td colspan="10" class="round-admin__empty">
              {{ loading ? '불러오는 중...' : '이 기간에 예약이 있는 출발일이 없습니다. (루트에 HOME 채널로 매칭된 상품 기준)' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ============ 출발일 상세 ============ -->
    <div v-else>
      <div class="round-admin__toolbar">
        <div class="round-admin__title">
          <button type="button" class="btn btn--sm" @click="backToList">← 목록</button>
          <h2 class="round-admin__h2">{{ info.routeName }} · {{ fmtDate(info.roundDate) }}</h2>
          <span :class="['badge', openBadge(info.seatOpenFlag).cls]">{{ openBadge(info.seatOpenFlag).label }}</span>
        </div>
        <div class="round-admin__toolbar-right">
          <label class="round-admin__label">투입 차량</label>
          <input v-model.number="busCountInput" type="number" min="1" max="20" class="round-admin__count" />
          <span class="round-admin__label">대</span>
          <button type="button" class="btn btn--sm" :disabled="saving || busCountInput === buses.length" @click="applyBusCount">적용</button>
          <span class="round-admin__sep" />
          <button type="button" class="btn btn--sm" :disabled="saving" @click="refreshWithAssign">새로고침</button>
          <button
            v-if="info.seatOpenFlag !== 'Y'"
            type="button"
            class="btn btn--primary btn--sm"
            :disabled="saving"
            @click="openSeatNow"
          >
            {{ info.seatOpenFlag === 'H' ? '좌석지정 오픈 재시도' : '좌석지정 지금 오픈' }}
          </button>
        </div>
      </div>

      <!-- 좌석지정 일정 -->
      <section class="round-admin__section round-admin__schedule">
        <div>
          <span class="round-admin__label">좌석지정 오픈</span>
          <b>{{ info.seatOpenAt ? fmtDateTime(info.seatOpenAt) : '출발 2일 전 안내 발송 후 자동 오픈' }}</b>
        </div>
        <div>
          <span class="round-admin__label">마감</span>
          <template v-if="!closeEditing">
            <b>{{ fmtDateTime(info.seatCloseAt) }}</b>
            <button type="button" class="btn btn--sm" @click="startCloseEdit">변경</button>
          </template>
          <template v-else>
            <input v-model="closeInput" type="datetime-local" />
            <button type="button" class="btn btn--sm btn--primary" :disabled="saving" @click="saveClose">저장</button>
            <button type="button" class="btn btn--sm" @click="closeEditing = false">취소</button>
          </template>
        </div>
        <div v-if="Number(info.unassignedCnt) > 0" class="round-admin__warn">
          배차되지 않은 예약 {{ info.unassignedCnt }}건 — [새로고침]을 누르면 명단 또는 1호차로 배차됩니다.
        </div>
      </section>

      <!-- 호차 탭 -->
      <div class="round-admin__tabs">
        <button
          v-for="b in buses"
          :key="b.busId"
          type="button"
          class="round-admin__tab"
          :class="{ 'round-admin__tab--on': b.busId === activeBusId }"
          @click="selectBus(b.busId)"
        >
          <b>{{ b.busSeq }}호차 <small v-if="b.departTime">{{ b.departTime }}</small></b>
          <span v-if="b.manifestAt" :class="{ 'round-admin__warn': Number(b.manifestPax) > Number(b.capCnt) || Number(b.manifestIssue) > 0 }">
            명단 {{ b.manifestPax }}명 / 정원 {{ b.capCnt }}<template v-if="Number(b.manifestIssue)"> · 확인 {{ b.manifestIssue }}</template>
          </span>
          <span v-else class="round-admin__warn">명단 없음 · 홈페이지 {{ b.assignedPax }}명</span>
          <span>좌석 확정 {{ b.bookedCnt }}<template v-if="Number(b.holdCnt)"> · 선택중 {{ b.holdCnt }}</template></span>
        </button>
      </div>

      <!-- 선택 호차: 좌석맵 -->
      <section v-if="activeBus" class="round-admin__section">
        <div class="round-admin__section-head">
          <h3 class="round-admin__h3">{{ activeBus.busSeq }}호차 좌석</h3>
          <div class="round-admin__tools">
            <label class="round-admin__label">출발시각</label>
            <input v-model="departInput" type="time" />
            <button type="button" class="btn btn--sm" :disabled="saving || departInput === (activeBus.departTime || '')" @click="saveDepartTime">저장</button>
            <span class="round-admin__sep" />
            <button type="button" class="btn btn--sm" @click="copyGuideLink(activeBus)">가이드 링크 복사</button>
            <button type="button" class="btn btn--sm" @click="openGuideSheet(activeBus)">좌석표 보기</button>
          </div>
        </div>

        <!-- 명단 -->
        <div class="round-admin__manifest">
          <div class="round-admin__manifest-head">
            <div>
              <b>탑승 명단</b>
              <template v-if="activeBus.manifestAt">
                <span class="round-admin__sub">
                  {{ fmtDateTime(activeBus.manifestAt) }} 업로드 · {{ activeBus.manifestRows }}팀 {{ activeBus.manifestPax }}명
                  <template v-if="activeBus.manifestTotal != null"> (엑셀 총인원 {{ activeBus.manifestTotal }}명)</template>
                </span>
                <span v-if="activeBus.manifestTotal != null && Number(activeBus.manifestTotal) !== Number(activeBus.manifestPax)" class="round-admin__warn">
                  · 총인원과 합계가 불일치합니다
                </span>
              </template>
              <span v-else class="round-admin__warn"> 업로드된 명단이 없습니다 (이 호차 배차는 명단에 없는 예약 → 1호차 기준)</span>
            </div>
            <div class="round-admin__tools">
              <input ref="manifestFile" type="file" accept=".xlsx,.xls" class="round-admin__file" @change="onManifestFile" />
              <button type="button" class="btn btn--sm btn--primary" :disabled="saving" @click="$refs.manifestFile.click()">
                {{ activeBus.manifestAt ? '명단 다시 올리기' : '명단 업로드' }}
              </button>
              <button type="button" class="btn btn--sm" @click="downloadTemplate">양식 받기</button>
              <button v-if="activeBus.manifestAt" type="button" class="btn btn--sm" @click="toggleManifest">
                {{ showManifest ? '명단 접기' : '명단 보기' }}
              </button>
            </div>
          </div>
          <!-- 호차 가이드 정보 (직접 입력, 명단 엑셀에 가이드 정보가 있으면 업로드할 때 그 내용으로 교체) -->
          <div class="round-admin__guide-edit">
            <label class="round-admin__label">가이드 정보</label>
            <textarea
              v-model="guideInput"
              rows="3"
              placeholder="가이드명, 연락처, 차량번호 등을 입력해 주십시오."
            ></textarea>
            <button
              type="button"
              class="btn btn--sm"
              :disabled="saving || guideInput === (activeBus.guideInfo || '')"
              @click="saveGuideInfo"
            >
              저장
            </button>
          </div>

          <table v-if="showManifest && activeBus.manifestAt" class="round-admin__table round-admin__table--compact">
            <thead>
              <tr>
                <th>No</th>
                <th>탑승자</th>
                <th>인원</th>
                <th>연락처</th>
                <th>비고</th>
                <th>예약번호</th>
                <th>구매채널</th>
                <th>좌석</th>
                <th>확인</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in manifest" :key="m.rowNo">
                <td>{{ m.rowNo }}</td>
                <td>
                  {{ m.paxName }}
                  <div v-if="m.passName" class="round-admin__pass">{{ m.passName }}</div>
                </td>
                <td>{{ m.paxCnt }}</td>
                <td>{{ m.telNo }}</td>
                <td>{{ m.memo }}</td>
                <td>{{ m.reqReserveCode || '-' }}</td>
                <td>{{ m.channel }}</td>
                <td>{{ m.seatNos || '-' }}</td>
                <td>
                  <span v-if="m.issue" class="badge badge--warn">{{ issueLabel(m.issue) }}</span>
                  <span v-else-if="m.auth === 'OK'" class="badge badge--member">멤버 · 지정대상</span>
                  <span v-else-if="m.matchedFlag === 'Y'" class="badge badge--off">홈페이지</span>
                  <span v-else class="round-admin__muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="moveFrom" class="round-admin__move">
          <b>{{ moveFrom.reqName || moveFrom.bookingNo }}</b> 님의 {{ moveFrom.busSeq }}호차 {{ moveFrom.seatNo }} 좌석 이동 중 —
          옮길 좌석을 선택해 주십시오. 다른 사람의 좌석을 선택하면 서로 바뀌고, 위 호차 탭을 바꾸면 다른 호차로도 옮길 수 있습니다.
          <button type="button" class="btn btn--sm" @click="moveFrom = null">이동 취소</button>
        </div>

        <div class="round-admin__seat-wrap">
          <div class="round-admin__grid">
            <div class="round-admin__driver">운전석</div>
            <div v-for="(row, rIdx) in seatRows" :key="rIdx" class="round-admin__row">
              <span class="round-admin__row-no">{{ rIdx + 1 }}열</span>
              <template v-for="seat in row" :key="seat.seatNo">
                <button
                  type="button"
                  class="round-admin__seat"
                  :class="seatClass(seat)"
                  :title="seatTitle(seat)"
                  @click="clickSeat(seat)"
                >
                  <span class="round-admin__seat-no">{{ seat.seatNo }}</span>
                  <span v-if="seat.bookingNo" class="round-admin__seat-who">{{ seatName(seat) }}</span>
                  <span v-if="seat.extraFee > 0 && !seat.bookingNo" class="round-admin__fee-dot">+</span>
                </button>
                <span v-if="seat.aisleAfter === 'Y'" class="round-admin__aisle" />
              </template>
            </div>
            <p v-if="seatRows.length === 0" class="round-admin__empty">좌석 정보가 없습니다.</p>
          </div>

          <!-- 선택 좌석 패널 -->
          <div v-if="pickSeat && !moveFrom" class="round-admin__panel">
            <div class="round-admin__panel-head">
              <strong>{{ activeBus.busSeq }}호차 {{ pickSeat.seatNo }}</strong>
              <span class="round-admin__sub">
                {{ gradeLabel(pickSeat.seatGrade) }}<template v-if="pickSeat.extraFee > 0"> · +{{ pickSeat.extraFee }}원</template>
              </span>
              <button type="button" class="btn btn--sm" @click="pickSeat = null">닫기</button>
            </div>

            <template v-if="pickSeat.status === 'booked' || pickSeat.status === 'hold'">
              <p class="round-admin__panel-line">
                <b>{{ pickSeat.reqName || '-' }}</b><br />
                {{ pickSeat.bookingNo }}<br />
                <span class="round-admin__sub">
                  {{ pickSeat.status === 'hold' ? '고객이 선택 중입니다 (세션이 끝나면 자동 해제)' : (pickSeat.assignType === 'A' ? '관리자 지정' : '고객 지정') }}
                </span>
              </p>
              <div class="round-admin__panel-actions">
                <button v-if="pickSeat.status === 'booked'" type="button" class="btn btn--sm btn--primary" @click="startMove(pickSeat)">좌석 이동</button>
                <button type="button" class="btn btn--sm btn--danger" :disabled="saving" @click="releaseSeat(pickSeat)">좌석 해제</button>
              </div>
            </template>

            <template v-else>
              <div class="round-admin__field">
                <label>이 좌석에 앉힐 예약</label>
                <select v-model="assignReqCode">
                  <option value="">예약 선택</option>
                  <option v-for="b in seatTargets" :key="b.reqReserveCode" :value="b.reqReserveCode">
                    {{ b.busSeq ? b.busSeq + '호차 · ' : '' }}{{ b.reqName }} · {{ b.reqReserveCode }} ({{ b.seatOwned }}/{{ b.seatCnt }}석)
                  </option>
                </select>
              </div>
              <button type="button" class="btn btn--sm btn--primary" :disabled="saving || !assignReqCode" @click="assignSeat">
                이 좌석에 지정
              </button>
              <p v-if="seatTargets.length === 0" class="round-admin__hint">좌석이 남은 예약이 없습니다.</p>

              <div class="round-admin__field round-admin__panel-grade">
                <label>좌석 등급</label>
                <div class="round-admin__inline">
                  <select v-model="gradeForm">
                    <option value="normal">지정가능</option>
                    <option value="blocked">지정불가</option>
                    <option value="memberOnly">유료회원전용</option>
                    <option value="preAssign">사전예약석</option>
                  </select>
                  <button type="button" class="btn btn--sm" :disabled="saving || gradeForm === pickSeat.seatGrade" @click="changeGrade">변경</button>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="round-admin__legend">
          <span class="legend-item"><i class="dot seat--normal" />지정가능</span>
          <span class="legend-item"><i class="dot seat--memberOnly" />유료회원전용</span>
          <span class="legend-item"><i class="dot seat--blocked" />지정불가</span>
          <span class="legend-item"><i class="dot seat--preAssign" />사전예약석</span>
          <span class="legend-item"><i class="dot seat--customer" />고객 지정</span>
          <span class="legend-item"><i class="dot seat--admin" />관리자 지정</span>
          <span class="legend-item"><i class="dot seat--hold" />고객 선택중</span>
        </div>
      </section>

      <!-- 예약 목록 -->
      <section class="round-admin__section">
        <div class="round-admin__section-head">
          <h3 class="round-admin__h3">예약 목록 <small>({{ filteredBookings.length }} / {{ bookings.length }}건 · 예약 순서)</small></h3>
          <div class="round-admin__tools">
            <input v-model.trim="keyword" type="text" placeholder="예약번호 / 예약자 / 연락처" />
            <select v-model="bookingFilter">
              <option value="all">전체</option>
              <option value="nobus">미배차</option>
              <option value="active">선택한 호차만</option>
              <option value="self">고객 지정대상</option>
              <option value="todo">좌석 미완료</option>
            </select>
          </div>
        </div>

        <div class="round-admin__bulk">
          <span>선택 {{ selected.length }}건</span>
          <select v-model.number="bulkBusId">
            <option :value="null">호차 선택</option>
            <option v-for="b in buses" :key="b.busId" :value="b.busId">{{ busLabel(b) }}</option>
          </select>
          <button type="button" class="btn btn--sm" :disabled="saving || !bulkBusId || !selected.length" @click="moveBulk">
            선택 예약을 이 호차로 옮기기
          </button>
          <span class="round-admin__hint">명단과 다르게 탑승시켜야 할 때만 옮겨 주십시오. 명단을 다시 등록하면 명단 기준으로 다시 배차됩니다.</span>
        </div>

        <table class="round-admin__table round-admin__table--compact">
          <thead>
            <tr>
              <th><input type="checkbox" :checked="allChecked" @change="toggleAll($event.target.checked)" /></th>
              <th>예약번호</th>
              <th>예약자</th>
              <th>인원</th>
              <th>좌석지정 자격</th>
              <th>명단</th>
              <th>호차</th>
              <th>상태</th>
              <th>좌석</th>
              <th>안내</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in filteredBookings" :key="b.reqReserveCode">
              <td><input v-model="selected" type="checkbox" :value="b.reqReserveCode" /></td>
              <td>
                {{ b.reqReserveCode }}
                <div class="round-admin__sub">{{ b.productName || b.reqPdtCode }}</div>
              </td>
              <td>
                {{ b.reqName }}
                <div v-if="b.passName" class="round-admin__pass">{{ b.passName }}</div>
                <div class="round-admin__sub">{{ b.reqTelNo }}</div>
              </td>
              <td>{{ b.reqTotCnt }}</td>
              <td><span :class="['badge', authBadge(b.authNow).cls]">{{ authBadge(b.authNow).label }}</span></td>
              <td>
                <span v-if="b.manifestBusSeq">{{ b.manifestBusSeq }}호차</span>
                <span v-else class="round-admin__muted">없음</span>
              </td>
              <td class="round-admin__nowrap">
                <select v-model.number="rowBus[b.reqReserveCode]">
                  <option :value="null">미배차</option>
                  <option v-for="bus in buses" :key="bus.busId" :value="bus.busId">{{ busLabel(bus) }}</option>
                </select>
                <button
                  v-if="rowBus[b.reqReserveCode] && rowBus[b.reqReserveCode] !== b.busId"
                  type="button"
                  class="btn btn--sm"
                  :disabled="saving"
                  @click="moveOne(b)"
                >
                  옮기기
                </button>
              </td>
              <td><span :class="['st', 'st--' + b.seatState]">{{ stateLabel(b.seatState) }}</span></td>
              <td>
                <template v-if="b.seatNos">{{ b.seatNos }}</template>
                <span v-else class="round-admin__muted">-</span>
                <div v-if="b.seatCnt" class="round-admin__sub">{{ b.seatOwned }} / {{ b.seatCnt }}석</div>
              </td>
              <td>
                <template v-if="b.selfFlag === 'Y'">
                  <span v-if="b.notiSentAt" class="round-admin__sub">
                    {{ notiLabel(b.notiResult) }} · {{ fmtDateTime(b.notiSentAt) }}
                  </span>
                  <button
                    v-else-if="canSendOne(b)"
                    type="button"
                    class="btn btn--sm"
                    :disabled="saving"
                    @click="sendOne(b)"
                  >
                    안내 발송
                  </button>
                  <span v-else class="round-admin__muted">오픈 시 발송</span>
                </template>
                <span v-else class="round-admin__muted">-</span>
              </td>
              <td>
                <button
                  v-if="Number(b.seatOwned) > 0"
                  type="button"
                  class="btn btn--sm btn--danger"
                  :disabled="saving"
                  @click="releaseBooking(b)"
                >
                  좌석 해제
                </button>
              </td>
            </tr>
            <tr v-if="filteredBookings.length === 0">
              <td colspan="11" class="round-admin__empty">
                {{ bookings.length ? '조건에 맞는 예약이 없습니다.' : '이 출발일에 홈페이지 예약이 없습니다.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

  </div>
</template>

<script>
import * as XLSX from 'xlsx';

// CALL 응답은 드라이버에 따라 [{...}] 또는 [[{...}], OkPacket] 형태라서 둘 다 처리
function firstRow(res) {
  const first = Array.isArray(res) ? res[0] : null;
  return Array.isArray(first) ? first[0] : first;
}

// 명단 엑셀 헤더 (이름으로 열을 찾으므로 순서가 바뀌어도 괜찮아요)
const MANIFEST_HEADERS = ['가이드 및 버스 정보', '총인원', '탑승자', '인원', '연락처', '비고', '예약번호', '구매채널'];

const MOVE_BUS_MSG = {
  NOTFOUND: '예약을 찾을 수 없습니다 (취소된 예약일 수 있습니다)',
  NOBUS: '호차 정보를 찾을 수 없습니다',
  NOTINROUND: '이 출발일(루트 HOME 상품 + 출발일)의 예약이 아닙니다',
};
const SEAT_MSG = {
  NOBUS: '이 예약은 아직 배차되지 않았습니다. 먼저 호차로 옮겨 주십시오',
  OTHERROUND: '다른 출발일의 좌석입니다',
  INSESSION: '고객이 지금 좌석을 선택하는 중입니다. 잠시 후 다시 시도해 주십시오',
  FULLSEAT: '이 예약의 인원만큼 이미 좌석이 지정되어 있습니다',
  NOSEAT: '좌석을 찾을 수 없습니다',
  NOTARGET: '옮길 좌석을 찾을 수 없습니다',
  TAKEN: '고객이 선택 중인 좌석입니다',
  EMPTY: '이미 비어 있는 좌석입니다',
  SAME: '같은 좌석입니다',
};

export default {
  name: 'Ma_BusRoundAdmin',

  data() {
    return {
      mode: 'list', // 'list' | 'detail'
      loading: false,
      saving: false,

      // 목록
      rounds: [],
      dateFrom: '',
      dateTo: '',
      noLayoutCnt: 0,

      // 상세
      roundId: null,
      info: {},
      buses: [],
      activeBusId: null,
      departInput: '',
      guideInput: '',
      busCountInput: 1,
      manifest: [],
      showManifest: false,
      bookings: [],
      rowBus: {}, // { 예약번호: 화면에서 고른 busId }
      keyword: '',
      bookingFilter: 'all',
      selected: [],
      bulkBusId: null,
      closeEditing: false,
      closeInput: '',

      // 좌석맵
      seats: [],
      pickSeat: null,
      moveFrom: null, // { busId, busSeq, seatNo, bookingNo, reqName }
      assignReqCode: '',
      gradeForm: 'normal',
    };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },
    activeBus() {
      return this.buses.find((b) => b.busId === this.activeBusId) || null;
    },
    seatRows() {
      const rows = [];
      this.seats.forEach((s) => {
        const rIdx = s.seatRow - 1;
        if (!rows[rIdx]) rows[rIdx] = [];
        rows[rIdx][s.seatOrder - 1] = s;
      });
      return rows.filter(Boolean).map((r) => r.filter(Boolean));
    },
    filteredBookings() {
      const k = this.keyword.toLowerCase();
      return this.bookings.filter((b) => {
        if (k && !`${b.reqReserveCode} ${b.reqName} ${b.reqTelNo}`.toLowerCase().includes(k)) return false;
        if (this.bookingFilter === 'nobus') return !b.busId;
        if (this.bookingFilter === 'active') return b.busId === this.activeBusId;
        if (this.bookingFilter === 'self') return b.authNow === 'OK';
        if (this.bookingFilter === 'todo') return b.seatState !== 'DONE';
        return true;
      });
    },
    allChecked() {
      return this.filteredBookings.length > 0
        && this.filteredBookings.every((b) => this.selected.includes(b.reqReserveCode));
    },
    // 빈 좌석에 앉힐 수 있는 예약: 배차됐고 좌석이 남은 예약 (보고 있는 호차 예약을 위로)
    seatTargets() {
      return this.bookings
        .filter((b) => b.busId && Number(b.seatOwned) < Number(b.seatCnt))
        .sort((a, b) => (a.busId === this.activeBusId ? 0 : 1) - (b.busId === this.activeBusId ? 0 : 1));
    },
  },

  created() {
    this.user = this.$store.state.user;
    const today = new Date();
    this.dateFrom = this.ymd(today);
    this.dateTo = this.ymd(new Date(today.getTime() + 30 * 86400000));
    this.loadList();
  },

  mounted() {
    // 권한없는 로그인 차단
    if (this.user.handlerGrade >= 20 || this.user.handlerGrade == undefined) {
      var swal = require('sweetalert2');
      swal.fire({
        title: '로그인 필요',
        text: '접근권한 확인불가.',
        icon: 'error'
      });
      this.$router.push({ path: '/' });
    }

    if (this.user.handlerId != undefined) {
      document.getElementById("admin").style.display = "block";
    }
    document.getElementById("user").style.display = "none";
  },

  methods: {
    // ---- 표시 유틸 ----
    ymd(d) {
      const p = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    // DB 날짜가 'YYYY-MM-DD ...' 문자열이든 ISO(UTC) 문자열이든 로컬 기준으로 표시
    toLocal(v) {
      if (!v) return null;
      const s = String(v);
      if (s.includes('T') && s.endsWith('Z')) return new Date(s);
      return new Date(s.replace(' ', 'T'));
    },
    fmtDate(v) {
      const d = this.toLocal(v);
      return d && !isNaN(d) ? this.ymd(d) : '-';
    },
    fmtDateTime(v) {
      const d = this.toLocal(v);
      if (!d || isNaN(d)) return '-';
      const p = (n) => String(n).padStart(2, '0');
      return `${this.ymd(d)} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    busLabel(b) {
      return `${b.busSeq}호차${b.departTime ? ' ' + b.departTime : ''}`;
    },
    openBadge(flag) {
      if (flag === 'Y') return { label: '오픈', cls: 'badge--on' };
      if (flag === 'H') return { label: '보류', cls: 'badge--warn' };
      return { label: '대기', cls: 'badge--off' };
    },
    authBadge(code) {
      return {
        OK: { label: '멤버 · 지정대상', cls: 'badge--member' },
        NO_MEMBER: { label: '일반', cls: 'badge--off' },
        MEM_EXPIRE: { label: '멤버(출발 전 만료)', cls: 'badge--warn' },
        NO_CHANNEL: { label: '외부채널', cls: 'badge--off' },
      }[code] || { label: code || '-', cls: 'badge--off' };
    },
    stateLabel(s) {
      return {
        NOBUS: '미배차',
        WAIT: '오픈 대기',
        OPEN: '지정 가능',
        ING: '고객 선택중',
        DONE: '좌석 완료',
        ADMIN: '관리자 배정',
      }[s] || s;
    },
    notiLabel(r) {
      return { OK: '발송', FAIL: '발송실패', SKIP: '발송생략(개발)' }[r] || r || '-';
    },
    gradeLabel(g) {
      return { normal: '지정가능', blocked: '지정불가', memberOnly: '유료회원전용', preAssign: '사전예약석' }[g] || g;
    },
    // 좌석표 표시명: 여권명 우선, 없으면 예약자명 (2줄까지 표시)
    seatName(seat) {
      return seat.passName || seat.reqName || '';
    },
    alertMsg(text, icon = 'warning') {
      const swal = require('sweetalert2');
      return swal.fire({ text, icon });
    },
    async confirmMsg(title, text, confirmButtonText = '확인') {
      const swal = require('sweetalert2');
      const ask = await swal.fire({
        title,
        text,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText,
        cancelButtonText: '취소',
      });
      return ask.isConfirmed;
    },

    // ---- 목록: 회차 자동 생성·배차 후 조회 ----
    async loadList() {
      this.loading = true;
      try {
        const sync = await this.$api('/api/proc_syncBusRounds', { param: [this.dateFrom, this.dateTo, this.user.userId] });
        const row = firstRow(sync);
        this.noLayoutCnt = row && row.result === 'OK' ? Number(row.noLayoutCnt) || 0 : 0;
        if (!row) console.log('proc_syncBusRounds 응답:', sync);

        const res = await this.$api('/api/getBusRoundList', { param: [this.dateFrom, this.dateTo] });
        this.rounds = Array.isArray(res) ? res : [];
      } finally {
        this.loading = false;
      }
    },

    // ---- 상세 ----
    async openRound(roundId) {
      this.roundId = roundId;
      this.mode = 'detail';
      this.selected = [];
      this.keyword = '';
      this.bookingFilter = 'all';
      this.pickSeat = null;
      this.moveFrom = null;
      this.closeEditing = false;
      await this.loadDetail(true);
    },

    backToList() {
      this.mode = 'list';
      this.loadList();
    },

    // 새로 들어온 예약을 자동 배차한 뒤 다시 불러옴
    async refreshWithAssign() {
      this.saving = true;
      try {
        const res = await this.$api('/api/proc_autoAssignBusRound', { param: [this.roundId, this.user.userId] });
        const row = firstRow(res);
        await this.loadDetail(false);
        if (row && Number(row.assignedCnt) > 0) {
          this.alertMsg(`새 예약 ${row.assignedCnt}건을 자동 배차했습니다.`, 'info');
        }
      } finally {
        this.saving = false;
      }
    },

    async loadDetail(resetBus) {
      const [infoRes, busRes] = await Promise.all([
        this.$api('/api/getBusRoundInfoAdmin', { param: [this.roundId] }),
        this.$api('/api/getBusRoundBuses', { param: [this.roundId] }),
      ]);
      this.info = (Array.isArray(infoRes) && infoRes[0]) || {};
      this.buses = Array.isArray(busRes) ? busRes : [];
      if (resetBus || !this.buses.some((b) => b.busId === this.activeBusId)) {
        this.activeBusId = this.buses.length ? this.buses[0].busId : null;
      }
      this.syncDepartInput();
      this.busCountInput = this.buses.length;
      await Promise.all([this.loadBookings(), this.loadSeats(), this.loadManifest()]);
    },

    async loadManifest() {
      if (!this.activeBusId || !this.showManifest) {
        this.manifest = [];
        return;
      }
      const res = await this.$api('/api/getBusManifest', { param: [this.activeBusId] });
      this.manifest = Array.isArray(res) ? res : [];
    },

    async toggleManifest() {
      this.showManifest = !this.showManifest;
      await this.loadManifest();
    },

    async loadBookings() {
      const res = await this.$api('/api/getBusRoundBookings', { param: [this.roundId, this.roundId] });
      const list = Array.isArray(res) ? res : [];
      const rowBus = {};
      list.forEach((b) => { rowBus[b.reqReserveCode] = b.busId || null; });
      this.rowBus = rowBus;
      this.bookings = list;
      this.selected = this.selected.filter((c) => list.some((b) => b.reqReserveCode === c));
    },

    async loadSeats() {
      if (!this.activeBusId) {
        this.seats = [];
        return;
      }
      const res = await this.$api('/api/getBusSeatMapAdmin', { param: [this.activeBusId] });
      this.seats = Array.isArray(res) ? res : [];
      if (this.pickSeat) {
        this.pickSeat = this.seats.find((s) => s.seatNo === this.pickSeat.seatNo) || null;
      }
    },

    async refreshAfterChange() {
      const [infoRes, busRes] = await Promise.all([
        this.$api('/api/getBusRoundInfoAdmin', { param: [this.roundId] }),
        this.$api('/api/getBusRoundBuses', { param: [this.roundId] }),
      ]);
      if (Array.isArray(infoRes) && infoRes[0]) this.info = infoRes[0];
      if (Array.isArray(busRes)) this.buses = busRes;
      this.busCountInput = this.buses.length;
      await Promise.all([this.loadBookings(), this.loadSeats(), this.loadManifest()]);
    },

    async selectBus(busId) {
      this.activeBusId = busId;
      this.pickSeat = null; // 이동 중(moveFrom)이면 유지 → 다른 호차 좌석으로 옮길 수 있음
      this.syncDepartInput();
      await Promise.all([this.loadSeats(), this.loadManifest()]);
    },

    // 호차 출발시각·가이드 정보 입력란을 현재 호차 값으로 맞춤
    syncDepartInput() {
      this.departInput = this.activeBus ? (this.activeBus.departTime || '') : '';
      this.guideInput = this.activeBus ? (this.activeBus.guideInfo || '') : '';
    },

    toggleAll(checked) {
      const codes = this.filteredBookings.map((b) => b.reqReserveCode);
      this.selected = checked
        ? Array.from(new Set([...this.selected, ...codes]))
        : this.selected.filter((c) => !codes.includes(c));
    },

    // ---- 투입 차량 수 / 출발시각 / 마감 / 오픈 ----
    async applyBusCount() {
      const n = Number(this.busCountInput);
      if (!Number.isInteger(n) || n < 1 || n > 20) {
        this.alertMsg('차량 수는 1~20대로 입력해 주십시오.');
        return;
      }
      const cur = this.buses.length;
      const ok = await this.confirmMsg(
        `투입 차량을 ${n}대로 변경하시겠습니까?`,
        n > cur
          ? `${cur + 1}~${n}호차가 루트 기본 레이어로 추가됩니다. 추가한 호차에는 명단을 등록해 주십시오.`
          : `${n + 1}호차부터는 사용하지 않습니다. 해당 호차에 배차된 예약은 1호차로 옮겨집니다.`,
        '적용'
      );
      if (!ok) {
        this.busCountInput = cur;
        return;
      }
      this.saving = true;
      try {
        const res = await this.$api('/api/proc_setBusCount', { param: [this.roundId, n, this.user.userId] });
        const row = firstRow(res);
        if (row && row.result === 'OK') {
          await this.loadDetail(false);
        } else if (row && row.result === 'INUSE') {
          this.busCountInput = cur;
          this.alertMsg(`${row.busSeqs}호차는 좌석이 지정되었거나 명단이 등록되어 있어 줄일 수 없습니다. 먼저 좌석을 해제하거나 명단을 다른 호차로 등록해 주십시오.`);
        } else if (row && row.result === 'NOLAYOUT') {
          this.alertMsg('루트에 기본 좌석 레이어가 없습니다. 루트 관리에서 먼저 지정해 주십시오.');
        } else {
          console.log('proc_setBusCount 응답:', res);
          this.alertMsg('차량 수를 변경하지 못했습니다: ' + ((row && row.result) || (res && res.error) || '알 수 없는 오류'), 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    async saveDepartTime() {
      this.saving = true;
      try {
        const res = await this.$api('/api/setBusDepartTime', { param: [this.departInput || '', this.user.userId, this.activeBusId] });
        if (res && res.affectedRows === 1) {
          await this.refreshAfterChange();
          this.syncDepartInput();
        } else {
          this.alertMsg('출발시각을 저장하지 못했습니다: ' + (res && res.error ? res.error : '알 수 없는 오류'), 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    async saveGuideInfo() {
      this.saving = true;
      try {
        const res = await this.$api('/api/setBusGuideInfo', { param: [this.guideInput.trim() || null, this.user.userId, this.activeBusId] });
        if (res && res.affectedRows === 1) {
          await this.refreshAfterChange();
          this.syncDepartInput();
        } else {
          this.alertMsg('가이드 정보를 저장하지 못했습니다: ' + (res && res.error ? res.error : '알 수 없는 오류'), 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    startCloseEdit() {
      const d = this.toLocal(this.info.seatCloseAt);
      const p = (n) => String(n).padStart(2, '0');
      this.closeInput = d && !isNaN(d) ? `${this.ymd(d)}T${p(d.getHours())}:${p(d.getMinutes())}` : '';
      this.closeEditing = true;
    },

    async saveClose() {
      if (!this.closeInput) return;
      const value = this.closeInput.replace('T', ' ') + ':00';
      this.saving = true;
      try {
        const res = await this.$api('/api/setBusRoundClose', { param: [value, this.user.userId, this.roundId] });
        if (res && res.affectedRows === 1) {
          this.closeEditing = false;
          await this.refreshAfterChange();
        } else {
          this.alertMsg('마감 시각을 변경하지 못했습니다: ' + (res && res.error ? res.error : '알 수 없는 오류'), 'error');
        }
      } finally {
        this.saving = false;
      }
    },

    // 배치 전에 바로 열거나, 보류(H)된 회차를 버스 추가 후 다시 열 때
    // 서버 라우트가 자동 배차 → 안내 발송 → 오픈 확정까지 처리 (app_scheduler 의 /api/busSeatOpenRound)
    async openSeatNow() {
      const ok = await this.confirmMsg(
        '좌석지정을 오픈하시겠습니까?',
        '좌석지정 대상 멤버에게 안내(알림톡·앱푸시)를 발송하고, 발송이 끝나면 좌석지정이 오픈됩니다.',
        '오픈'
      );
      if (!ok) return;
      this.saving = true;
      try {
        const res = await this.$api('/api/busSeatOpenRound', { param: [this.roundId, this.user.userId] });
        if (res && res.result === 'OPEN') {
          this.alertMsg(`좌석지정을 오픈했습니다. (발송 ${res.sent} · 실패 ${res.failed} · 생략 ${res.skipped})`, 'success');
        } else if (res && res.result === 'HOLD') {
          this.alertMsg(`배차되지 않은 예약이 ${res.unassignedCnt}건 있어 오픈하지 않았습니다. [새로고침] 후 다시 시도해 주십시오.`);
        } else if (res && res.result === 'CLOSED') {
          this.alertMsg('이미 좌석지정 마감 시각이 지났습니다.');
        } else {
          console.log('busSeatOpenRound 응답:', res);
          this.alertMsg('오픈하지 못했습니다: ' + ((res && (res.message || res.result)) || '알 수 없는 오류'), 'error');
        }
        await this.refreshAfterChange();
      } finally {
        this.saving = false;
      }
    },

    // ---- 명단 업로드 ----
    issueLabel(code) {
      return { NOCODE: '예약번호 없음', UNKNOWN: '모르는 예약번호', DUP: '다른 호차와 중복' }[code] || code;
    },

    // 엑셀 → { guideInfo, total, rows } (헤더 이름으로 열을 찾음)
    parseManifest(sheetRows) {
      const norm = (v) => String(v == null ? '' : v).replace(/\s+/g, '');
      const headerIdx = sheetRows.findIndex((r) => {
        const cells = r.map(norm);
        return cells.includes('탑승자') && cells.includes('인원');
      });
      if (headerIdx < 0) return { error: "'탑승자'와 '인원' 열이 있는 헤더 행을 찾지 못했습니다. 양식을 확인해 주십시오." };

      const header = sheetRows[headerIdx].map(norm);
      const col = (name) => header.indexOf(norm(name));
      const cGuide = header.findIndex((h) => h.includes('가이드'));
      const cTotal = col('총인원');
      const cName = col('탑승자');
      const cCnt = col('인원');
      const cTel = col('연락처');
      const cMemo = col('비고');
      const cCode = col('예약번호');
      const cCh = col('구매채널');
      const cell = (r, c) => (c >= 0 && r[c] != null ? String(r[c]).trim() : '');

      const body = sheetRows.slice(headerIdx + 1);
      const firstVal = (c) => {
        const hit = body.find((r) => cell(r, c) !== '');
        return hit ? cell(hit, c) : '';
      };
      const guideInfo = firstVal(cGuide);
      const totalText = firstVal(cTotal).replace(/[^0-9]/g, '');

      const rows = [];
      body.forEach((r) => {
        const paxName = cell(r, cName);
        if (!paxName) return;
        let telNo = cell(r, cTel);
        if (/^1\d{8,9}$/.test(telNo)) telNo = '0' + telNo; // 엑셀이 숫자로 바꿔 앞 0이 빠진 경우
        rows.push({
          paxName,
          paxCnt: parseInt(cell(r, cCnt).replace(/[^0-9]/g, ''), 10) || 0,
          telNo,
          memo: cell(r, cMemo),
          reqReserveCode: cell(r, cCode).replace(/\s+/g, ''),
          channel: cell(r, cCh),
        });
      });
      if (rows.length === 0) return { error: '탑승자 행이 없습니다.' };
      return { guideInfo, total: totalText ? Number(totalText) : null, rows };
    },

    async onManifestFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file || !this.activeBus) return;

      let parsed;
      try {
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        parsed = this.parseManifest(XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }));
      } catch (err) {
        console.log('manifest parse error:', err);
        this.alertMsg('엑셀 파일을 읽지 못했습니다. 파일 형식을 확인해 주십시오.', 'error');
        return;
      }
      if (parsed.error) {
        this.alertMsg(parsed.error);
        return;
      }

      const paxSum = parsed.rows.reduce((sum, r) => sum + r.paxCnt, 0);
      const codeCnt = parsed.rows.filter((r) => r.reqReserveCode).length;
      const totalLine = parsed.total == null
        ? '엑셀에 총인원이 없습니다'
        : (parsed.total === paxSum ? `총인원 ${parsed.total}명 일치` : `⚠️ 엑셀 총인원 ${parsed.total}명 ≠ 인원 합계 ${paxSum}명`);
      const swal = require('sweetalert2');
      const ask = await swal.fire({
        title: `${this.busLabel(this.activeBus)} 명단 업로드`,
        html: `<div style="text-align:left;font-size:13px;line-height:1.7">
                 파일: ${file.name}<br>
                 ${parsed.rows.length}팀 · ${paxSum}명 (${totalLine})<br>
                 예약번호 있는 행 ${codeCnt}건<br>
                 ${this.activeBus.manifestAt ? '<b>기존 명단은 이 파일로 교체됩니다.</b><br>' : ''}
                 다른 호차에 있던 예약이 이 호차로 옮겨지면 기존 좌석은 해제됩니다.
               </div>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '업로드',
        cancelButtonText: '취소',
      });
      if (!ask.isConfirmed) return;

      this.saving = true;
      try {
        const res = await this.$api('/api/proc_uploadBusManifest', {
          // 엑셀에 가이드 정보가 없으면 직접 입력한 가이드 정보를 유지
          param: [this.activeBusId, parsed.guideInfo || this.activeBus.guideInfo || null, parsed.total, JSON.stringify(parsed.rows), this.user.userId],
        });
        const row = firstRow(res);
        if (!(row && row.result === 'OK')) {
          console.log('proc_uploadBusManifest 응답:', res);
          this.alertMsg('명단을 등록하지 못했습니다: ' + ((row && row.result) || (res && res.error) || '알 수 없는 오류'), 'error');
          return;
        }
        this.showManifest = true;
        await this.refreshAfterChange();
        this.syncDepartInput();

        const warns = [];
        if (Number(row.noCodeCnt)) warns.push(`홈페이지 채널인데 예약번호가 없는 행 ${row.noCodeCnt}건 (멤버인 경우 좌석지정 대상에서 제외됩니다)`);
        if (Number(row.unknownCnt)) warns.push(`이 출발일의 홈페이지 예약이 아닌 예약번호 ${row.unknownCnt}건`);
        if (Number(row.dupCnt)) warns.push(`다른 호차 명단에도 있는 예약번호 ${row.dupCnt}건`);
        if (Number(row.notListedCnt)) warns.push(`이 호차에 배차돼 있지만 명단에는 없는 홈페이지 예약 ${row.notListedCnt}건`);
        if (row.totalCnt != null && Number(row.totalCnt) !== Number(row.paxSum)) warns.push(`엑셀 총인원 ${row.totalCnt}명과 인원 합계 ${row.paxSum}명이 다릅니다`);

        await swal.fire({
          title: '명단을 등록했습니다',
          html: `<div style="text-align:left;font-size:13px;line-height:1.7">
                   ${row.rowCnt}팀 · ${row.paxSum}명<br>
                   홈페이지 예약 연결 ${row.matchedCnt}건 (멤버 지정대상 ${row.memberCnt}건)<br>
                   이 호차로 옮겨온 예약 ${row.movedCnt}건${Number(row.releasedSeatCnt) ? ` · 해제된 좌석 ${row.releasedSeatCnt}석` : ''}
                   ${warns.length ? '<hr><b>확인이 필요합니다</b><br>' + warns.join('<br>') : ''}
                 </div>`,
          icon: warns.length ? 'warning' : 'success',
        });
      } finally {
        this.saving = false;
      }
    },

    downloadTemplate() {
      const aoa = [
        MANIFEST_HEADERS,
        ['가이드 이름 / 서포트\n차량번호 좌석구성\n기사 이름 연락처', '0명', '홍길동(예시)', 2, '010-0000-0000', '', '홈페이지 예약번호', ''],
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 28 }, { wch: 8 }, { wch: 14 }, { wch: 6 }, { wch: 16 }, { wch: 14 }, { wch: 20 }, { wch: 10 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '명단');
      XLSX.writeFile(wb, '버스명단_양식.xlsx');
    },

    // ---- 가이드 좌석표 링크 ----
    guideUrl(bus) {
      return `${window.location.origin}/busGuideSheet?t=${bus.guideToken}`;
    },
    async copyGuideLink(bus) {
      const url = this.guideUrl(bus);
      try {
        await navigator.clipboard.writeText(url);
        this.alertMsg(`${this.busLabel(bus)} 가이드 좌석표 링크 복사완료.\n${url}`, 'success');
      } catch (e) {
        window.prompt('아래 링크를 복사해서 가이드에게 전달해 주십시오.', url);
      }
    },
    openGuideSheet(bus) {
      window.open(this.guideUrl(bus), '_blank');
    },

    // ---- 예약 호차 옮기기 ----
    async moveBus(b, busId) {
      const res = await this.$api('/api/proc_assignBusToReserve', { param: [b.reqReserveCode, busId, this.user.userId] });
      const row = firstRow(res);
      if (!row) return { ok: false, msg: (res && res.error) || '응답 없음' };
      if (row.result === 'OK') return { ok: true, needNoti: row.needNoti === 'Y', released: Number(row.releasedCnt) || 0 };
      return { ok: false, msg: MOVE_BUS_MSG[row.result] || row.result };
    },

    async moveOne(b) {
      const target = this.buses.find((x) => x.busId === this.rowBus[b.reqReserveCode]);
      if (Number(b.seatOwned) > 0) {
        const ok = await this.confirmMsg(
          `${target ? this.busLabel(target) : ''}로 옮기시겠습니까?`,
          `${b.reqName} 님 일행의 기존 좌석(${b.seatNos})은 해제됩니다. 옮긴 뒤 좌석을 다시 지정해 주십시오.`,
          '옮기기'
        );
        if (!ok) {
          this.rowBus[b.reqReserveCode] = b.busId || null;
          return;
        }
      }
      this.saving = true;
      try {
        const r = await this.moveBus(b, this.rowBus[b.reqReserveCode]);
        if (!r.ok) {
          this.alertMsg(`${b.reqReserveCode}: ${r.msg}`);
          this.rowBus[b.reqReserveCode] = b.busId || null;
          return;
        }
        await this.refreshAfterChange();
        if (r.needNoti) await this.askSendOne(b.reqReserveCode);
      } finally {
        this.saving = false;
      }
    },

    async moveBulk() {
      const targets = this.bookings.filter((b) => this.selected.includes(b.reqReserveCode) && b.busId !== this.bulkBusId);
      const bus = this.buses.find((x) => x.busId === this.bulkBusId);
      const seated = targets.filter((b) => Number(b.seatOwned) > 0).length;
      const ok = await this.confirmMsg(
        `${targets.length}건을 ${bus ? this.busLabel(bus) : ''}로 옮기시겠습니까?`,
        seated ? `이미 좌석이 있는 ${seated}건은 기존 좌석이 해제됩니다.` : '선택한 예약의 일행 전체가 옮겨집니다.',
        '옮기기'
      );
      if (!ok) return;

      this.saving = true;
      const fails = [];
      const notis = [];
      let okCnt = 0;
      try {
        for (const b of targets) {
          const r = await this.moveBus(b, this.bulkBusId);
          if (r.ok) {
            okCnt += 1;
            if (r.needNoti) notis.push(b.reqReserveCode);
          } else {
            fails.push(`${b.reqReserveCode}: ${r.msg}`);
          }
        }
      } finally {
        this.saving = false;
      }
      this.selected = [];
      await this.refreshAfterChange();

      const swal = require('sweetalert2');
      await swal.fire({
        title: `옮김 ${okCnt}건 / 실패 ${fails.length}건`,
        html: fails.length ? `<div style="text-align:left;font-size:13px;">${fails.join('<br>')}</div>` : '',
        icon: fails.length ? 'warning' : 'success',
      });
      if (notis.length) {
        this.alertMsg(`이미 오픈된 회차라 안내가 필요한 예약이 ${notis.length}건 있습니다. 목록의 [안내 발송]을 눌러 주십시오.`, 'info');
      }
    },

    // ---- 안내 개별 발송 (오픈 후 늦게 배차된 지정대상) ----
    canSendOne(b) {
      return this.info.seatOpenFlag === 'Y' && b.busId && !b.notiSentAt && b.seatState !== 'DONE';
    },

    async askSendOne(reqCode) {
      const ok = await this.confirmMsg(
        '좌석지정 안내를 발송하시겠습니까?',
        `${reqCode} 예약은 이미 오픈된 회차에 배차되어 개별 안내가 필요합니다.`,
        '발송'
      );
      if (ok) await this.sendOneByCode(reqCode);
    },

    async sendOne(b) {
      this.saving = true;
      try {
        await this.sendOneByCode(b.reqReserveCode);
      } finally {
        this.saving = false;
      }
    },

    async sendOneByCode(reqCode) {
      const res = await this.$api('/api/busSeatSendOne', { param: [reqCode, this.roundId] });
      const r = res && res.result;
      if (r === 'OK') this.alertMsg('안내를 발송했습니다.', 'success');
      else if (r === 'SKIP') this.alertMsg('개발 환경이라 발송은 생략하고 기록만 남겼습니다.', 'info');
      else if (r === 'NOTARGET') this.alertMsg('안내 대상이 아닙니다. (이미 발송했거나 지정을 마쳤습니다)');
      else this.alertMsg('안내를 발송하지 못했습니다: ' + (r || '알 수 없는 오류'), 'error');
      await this.loadBookings();
    },

    // ---- 좌석 해제 (예약 단위) ----
    async releaseBooking(b) {
      const ok = await this.confirmMsg(
        '이 예약의 좌석을 모두 해제하시겠습니까?',
        `${b.reqName} (${b.reqReserveCode}) · ${b.seatNos}\n해제하면 마감 전까지 지정대상 고객이 다시 선택할 수 있습니다.`,
        '해제'
      );
      if (!ok) return;
      this.saving = true;
      try {
        await this.$api('/api/proc_adminReleaseBusSeats', { param: [b.reqReserveCode, this.user.userId] });
        await this.refreshAfterChange();
      } finally {
        this.saving = false;
      }
    },

    // ---- 좌석맵 ----
    seatClass(seat) {
      const c = [];
      if (seat.status === 'booked') c.push(seat.assignType === 'A' ? 'seat--admin' : 'seat--customer');
      else if (seat.status === 'hold') c.push('seat--hold');
      else c.push('seat--' + seat.seatGrade);
      if (this.pickSeat && this.pickSeat.seatNo === seat.seatNo) c.push('seat--active');
      if (this.moveFrom && this.moveFrom.busId === this.activeBusId && this.moveFrom.seatNo === seat.seatNo) c.push('seat--moving');
      return c;
    },

    seatTitle(seat) {
      if (seat.bookingNo) return `${seat.seatNo} · ${seat.reqName || ''}${seat.passName ? ' / ' + seat.passName : ''} (${seat.bookingNo})`;
      return `${seat.seatNo} · ${this.gradeLabel(seat.seatGrade)}${seat.extraFee > 0 ? ' · +' + seat.extraFee + '원' : ''}`;
    },

    async clickSeat(seat) {
      if (this.moveFrom) {
        if (this.moveFrom.busId === this.activeBusId && seat.seatNo === this.moveFrom.seatNo) {
          this.moveFrom = null;
          return;
        }
        if (seat.status === 'hold') {
          this.alertMsg('고객이 선택 중인 좌석입니다. 다른 좌석을 선택해 주십시오.', 'info');
          return;
        }
        if (seat.status === 'booked') {
          const ok = await this.confirmMsg(
            '좌석을 맞바꾸시겠습니까?',
            `${this.moveFrom.reqName || this.moveFrom.bookingNo} 님과 ${seat.reqName || seat.bookingNo} 님의 좌석이 서로 바뀝니다.`,
            '맞바꾸기'
          );
          if (!ok) return;
        }
        await this.doMove(seat);
        return;
      }
      this.pickSeat = seat;
      this.assignReqCode = '';
      this.gradeForm = seat.seatGrade;
    },

    async assignSeat() {
      this.saving = true;
      try {
        const res = await this.$api('/api/proc_adminAssignBusSeat', {
          param: [this.activeBusId, this.pickSeat.seatNo, this.assignReqCode, this.user.userId],
        });
        const row = firstRow(res);
        if (row && row.result === 'OK') {
          this.assignReqCode = '';
          await this.refreshAfterChange();
        } else {
          this.alertMsg((row && SEAT_MSG[row.result]) || '지정하지 못했습니다: ' + ((res && res.error) || '알 수 없는 오류'));
        }
      } finally {
        this.saving = false;
      }
    },

    async releaseSeat(seat) {
      const ok = await this.confirmMsg(
        `${seat.seatNo} 좌석을 해제하시겠습니까?`,
        seat.reqName ? `${seat.reqName} (${seat.bookingNo})` : '',
        '해제'
      );
      if (!ok) return;
      this.saving = true;
      try {
        const res = await this.$api('/api/proc_adminReleaseBusSeat', { param: [this.activeBusId, seat.seatNo, this.user.userId] });
        const row = firstRow(res);
        if (row && row.result !== 'OK') this.alertMsg(SEAT_MSG[row.result] || row.result);
        this.pickSeat = null;
        await this.refreshAfterChange();
      } finally {
        this.saving = false;
      }
    },

    startMove(seat) {
      this.moveFrom = {
        busId: this.activeBusId,
        busSeq: this.activeBus ? this.activeBus.busSeq : '',
        seatNo: seat.seatNo,
        bookingNo: seat.bookingNo,
        reqName: seat.reqName,
      };
      this.pickSeat = null;
    },

    async doMove(target) {
      const from = this.moveFrom;
      this.saving = true;
      try {
        const res = await this.$api('/api/proc_adminMoveBusSeat', {
          param: [from.busId, from.seatNo, this.activeBusId, target.seatNo, this.user.userId],
        });
        const row = firstRow(res);
        if (!(row && row.result === 'OK')) {
          this.alertMsg((row && SEAT_MSG[row.result]) || '이동하지 못했습니다. 원래 좌석은 그대로 유지됩니다.');
        }
        this.moveFrom = null;
        await this.refreshAfterChange();
      } finally {
        this.saving = false;
      }
    },

    async changeGrade() {
      this.saving = true;
      try {
        const res = await this.$api('/api/setBusSeatGrade', {
          param: [this.gradeForm, this.user.userId, this.activeBusId, this.pickSeat.seatNo],
        });
        if (res && res.affectedRows === 1) {
          await this.refreshAfterChange();
        } else {
          this.alertMsg('빈 좌석만 등급을 변경할 수 있습니다.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.round-admin {
  padding: 20px;
  width: 80%;
  margin: 0 auto;
}

.round-admin__toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.round-admin__toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.round-admin__title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.round-admin__h2 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}
.round-admin__h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 10px;
}
.round-admin__h3 small {
  font-weight: 400;
  color: #999;
}

.round-admin__section {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}
.round-admin__section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.round-admin__tools {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.round-admin__tools input,
.round-admin__tools select,
.round-admin__bulk select,
.round-admin__filter input,
.round-admin__schedule input,
.round-admin__table--compact select {
  padding: 4px 8px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 12px;
}
.round-admin__count {
  width: 52px;
  padding: 4px 6px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 12px;
}
.round-admin__file {
  display: none;
}
.round-admin__manifest {
  border: 1px dashed #d0d0d0;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  font-size: 13px;
}
.round-admin__manifest-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}
.round-admin__manifest-head .round-admin__tools {
  margin-bottom: 0;
}
.round-admin__guide-edit {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 0;
}
.round-admin__guide-edit textarea {
  flex: 1;
  max-width: 520px;
  padding: 6px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  resize: vertical;
}
.round-admin__guide-info {
  margin: 8px 0;
  padding: 8px 10px;
  background: #f7f7f5;
  border-radius: 6px;
  font-family: inherit;
  font-size: 12px;
  white-space: pre-wrap;
}
.round-admin__sep {
  width: 1px;
  height: 18px;
  background: #e2e2e2;
  margin: 0 4px;
}

.round-admin__field label {
  display: block;
  font-size: 12px;
  color: #777;
  margin-bottom: 4px;
}
.round-admin__field select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-size: 13px;
  box-sizing: border-box;
}

.round-admin__filter {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}
.round-admin__filter label {
  font-size: 12px;
  color: #777;
}

.round-admin__schedule {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  font-size: 13px;
}
.round-admin__schedule > div {
  display: flex;
  align-items: center;
  gap: 6px;
}
.round-admin__label {
  font-size: 12px;
  color: #777;
}

.round-admin__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.round-admin__table th,
.round-admin__table td {
  padding: 10px 12px;
  border-bottom: 1px solid #e2e2e2;
  text-align: left;
  vertical-align: top;
}
.round-admin__table--compact th,
.round-admin__table--compact td {
  padding: 7px 8px;
}
.round-admin__nowrap {
  white-space: nowrap;
}
.round-admin__empty {
  text-align: center;
  color: #999;
  padding: 24px;
}
.round-admin__sub {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
}
.round-admin__pass {
  font-size: 11px;
  color: #555;
  margin-top: 1px;
  letter-spacing: 0.2px;
}
.round-admin__muted {
  color: #bbb;
  font-size: 12px;
}
.round-admin__warn {
  color: #b5541f;
  font-weight: 600;
}
.round-admin__hint {
  font-size: 11px;
  color: #999;
  margin: 0;
}
.round-admin__hint--top {
  font-size: 12px;
  margin: 0 0 10px;
}

.round-admin__bulk {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
}

.round-admin__tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.round-admin__tab {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  padding: 8px 14px;
  border: 1px solid #d0d0d0;
  border-radius: 10px;
  background: #fff;
  font-size: 12px;
  color: #666;
  cursor: pointer;
}
.round-admin__tab b {
  font-size: 14px;
  color: #333;
}
.round-admin__tab b small {
  font-size: 12px;
  font-weight: 500;
  color: #2f7d5f;
  margin-left: 4px;
}
.round-admin__tab--on {
  border-color: #2f7d5f;
  background: #f0f8f4;
}

/* ---- 좌석맵 ---- */
.round-admin__move {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  background: #faeeda;
  font-size: 13px;
}
.round-admin__seat-wrap {
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
}
.round-admin__grid {
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 16px;
}
.round-admin__driver {
  width: 52px;
  margin: 0 0 12px auto;
  padding: 3px 0;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  text-align: center;
  font-size: 10px;
  color: #999;
}
.round-admin__row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.round-admin__row-no {
  font-size: 11px;
  color: #999;
  width: 28px;
}
.round-admin__seat {
  position: relative;
  flex: 0 0 auto;
  width: 60px;
  height: 54px;
  padding: 2px 3px;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
  background: #fff;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.1;
}
.round-admin__seat-no {
  font-size: 11px;
  font-weight: 600;
}
.round-admin__seat-who {
  width: 100%;
  margin-top: 2px;
  font-size: 9px;
  line-height: 1.15;
  text-align: center;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.round-admin__fee-dot {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #d64545;
  color: #fff;
  font-size: 9px;
  line-height: 12px;
}
.round-admin__aisle {
  width: 16px;
}

.seat--normal { background: #fff; color: #555; }
.seat--blocked { background: #444441; color: #fff; border-color: #444441; }
.seat--memberOnly { background: #faeeda; color: #854f0b; border-color: #f2cf94; }
.seat--preAssign { background: #eeedfe; color: #534ab7; border-color: #cecbf6; }
.seat--customer { background: #2f7d5f; color: #fff; border-color: #2f7d5f; }
.seat--admin { background: #9fd3b8; color: #1d4d39; border-color: #6fb893; }
.seat--hold { background: #fff6dd; color: #8a6a10; border-color: #e0b94a; border-style: dashed; }
.round-admin__seat.seat--active { outline: 2px solid #2b6cb0; outline-offset: 1px; }
.round-admin__seat.seat--moving { outline: 2px dashed #b5541f; outline-offset: 1px; }

.round-admin__panel {
  flex: 0 0 260px;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  padding: 14px;
  font-size: 13px;
}
.round-admin__panel-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.round-admin__panel-head strong {
  font-size: 15px;
}
.round-admin__panel-head .btn {
  margin-left: auto;
}
.round-admin__panel-line {
  line-height: 1.6;
  margin: 0 0 10px;
}
.round-admin__panel-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.round-admin__panel-grade {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.round-admin__inline {
  display: flex;
  gap: 6px;
}
.round-admin__inline select {
  flex: 1;
}

.round-admin__legend {
  display: flex;
  gap: 14px;
  margin-top: 12px;
  flex-wrap: wrap;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #666;
}
.dot {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid #d0d0d0;
}

/* ---- 상태 표시 ---- */
.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}
.badge--on {
  background: #e1f5ee;
  color: #0f6e56;
}
.badge--off {
  background: #f1efe8;
  color: #5f5e5a;
}
.badge--warn {
  background: #fdecdf;
  color: #b5541f;
}
.badge--member {
  background: #faeeda;
  color: #854f0b;
}

.st {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f1efe8;
  color: #5f5e5a;
  white-space: nowrap;
}
.st--NOBUS { background: #fdecdf; color: #b5541f; }
.st--OPEN { background: #e8f1fb; color: #2b6cb0; }
.st--ING { background: #fff6dd; color: #8a6a10; }
.st--DONE { background: #e1f5ee; color: #0f6e56; }

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn--primary {
  background: #2f7d5f;
  border-color: #2f7d5f;
  color: #fff;
}
.btn--primary:disabled {
  background: #ccc;
  border-color: #ccc;
  opacity: 1;
}
.btn--sm {
  padding: 4px 10px;
  font-size: 12px;
}
.btn--danger {
  color: #d64545;
  border-color: #f0c6c6;
}
</style>